import * as THREE from 'three';
import { settings } from '../../app/settings';

interface P {
  alive: boolean;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  max: number;
  size: number;
  gravity: number;
  spin: number;
}

/**
 * Sistema de partículas con un único InstancedMesh y un pool fijo: no se crean ni destruyen
 * objetos por fotograma. Se usa para confeti, pintura, chispas, polvo y destellos.
 */
export class Particles {
  readonly mesh: THREE.InstancedMesh;
  private pool: P[];
  private next = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private s = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(private max = 600) {
    this.mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 0.3), new THREE.MeshBasicMaterial({ vertexColors: false }), max);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.pool = Array.from({ length: max }, () => ({ alive: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), life: 0, max: 1, size: 0.1, gravity: 9, spin: 0 }));
    for (let i = 0; i < max; i++) {
      this.mesh.setColorAt(i, this.c.setHex(0xffffff));
      this.mesh.setMatrixAt(i, this.m.makeScale(0, 0, 0));
    }
  }

  burst(
    at: THREE.Vector3 | [number, number, number],
    opts: { count?: number; colors?: number[]; speed?: number; up?: number; life?: number; size?: number; gravity?: number; spread?: number } = {},
  ) {
    const reduce = settings.get().reduceEffects;
    const count = Math.round((opts.count ?? 20) * (reduce ? 0.35 : 1));
    const colors = opts.colors ?? [0xff006e, 0xffbe0b, 0x3a86ff, 0x06d6a0, 0x8338ec];
    const p0 = Array.isArray(at) ? new THREE.Vector3(...at) : at;
    for (let i = 0; i < count; i++) {
      const p = this.pool[this.next];
      const idx = this.next;
      this.next = (this.next + 1) % this.max;
      const speed = (opts.speed ?? 4) * (0.5 + Math.random());
      const a = Math.random() * Math.PI * 2;
      const spread = opts.spread ?? 1;
      p.alive = true;
      p.pos.copy(p0);
      p.vel.set(Math.cos(a) * speed * spread, (opts.up ?? 4) * (0.6 + Math.random() * 0.8), Math.sin(a) * speed * spread);
      p.life = p.max = (opts.life ?? 1) * (0.7 + Math.random() * 0.6);
      p.size = (opts.size ?? 0.12) * (0.6 + Math.random() * 0.8);
      p.gravity = opts.gravity ?? 9;
      p.spin = (Math.random() - 0.5) * 12;
      this.mesh.setColorAt(idx, this.c.setHex(colors[i % colors.length]));
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  /** Lluvia de confeti alrededor de un punto (eventos de fiesta). */
  rain(center: THREE.Vector3, radius: number, count: number) {
    for (let i = 0; i < count; i++) {
      const p = new THREE.Vector3(center.x + (Math.random() - 0.5) * radius * 2, center.y + 8 + Math.random() * 4, center.z + (Math.random() - 0.5) * radius * 2);
      this.burst(p, { count: 1, speed: 0.4, up: 0, life: 3, gravity: 2.5 });
    }
  }

  update(dt: number) {
    for (let i = 0; i < this.max; i++) {
      const p = this.pool[i];
      if (!p.alive) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        this.mesh.setMatrixAt(i, this.m.makeScale(0, 0, 0));
        continue;
      }
      p.vel.y -= p.gravity * dt;
      p.pos.addScaledVector(p.vel, dt);
      if (p.pos.y < 0.03) {
        p.pos.y = 0.03;
        p.vel.multiplyScalar(0.4);
      }
      const k = Math.min(1, p.life / (p.max * 0.4));
      this.e.set(p.life * p.spin, p.life * p.spin * 0.7, 0);
      this.q.setFromEuler(this.e);
      this.s.setScalar(p.size * k);
      this.mesh.setMatrixAt(i, this.m.compose(p.pos, this.q, this.s));
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
